/**
 * Geschäftsbereich Detail
 * View and edit Geschäftsbereich settings, license, and users
 */

import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Building2,
  Users,
  Key,
  Edit,
  Trash2,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useToast,
} from "@jourfx/ui";
import { SuperAdminGuard } from "../../components/SuperAdminGuard";
import { useDateFormat } from "../../hooks/useDateFormat";

type Id<T extends string> = string & { __tableName: T };
type OrganizationType = "md" | "clinic" | "practice" | "insurance" | "other";

const DEFAULT_SELF_REGISTRATION_NOTIFICATION_EMAILS = {
  plastischeChirurgie: "videobegutachtung_krankenhaus@md-bb.org",
  auOthers: "videobegutachtung_ambulant@md-bb.org",
  pflegebegutachtung: "videobegutachtung_pflege@md-bb.org",
};

function getOrganizationStatusLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  status: string
): string {
  switch (status) {
    case "active":
      return t("status.active");
    case "trial":
      return t("status.trial");
    case "suspended":
      return t("status.suspended");
    case "archived":
      return t("status.archived");
    case "inactive":
      return t("status.inactive");
    default:
      return status;
  }
}

function getOrganizationTypeLabel(t: (key: string) => string, type: string): string {
  switch (type) {
    case "md":
      return t("create.types.md");
    case "clinic":
      return t("create.types.clinic");
    case "practice":
      return t("create.types.practice");
    case "insurance":
      return t("create.types.insurance");
    case "other":
      return t("create.types.other");
    default:
      return type;
  }
}

function OrganizationDetailContent() {
  const { t } = useTranslation("organizations");
  const { t: tc } = useTranslation("common");
  const { formatShortDate } = useDateFormat();
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const navigate = useNavigate();
  const { toast } = useToast();
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [versichertenProfileRetentionDays, setVersichertenProfileRetentionDays] = useState("180");
  const [isSavingRetention, setIsSavingRetention] = useState(false);
  const [selfRegistrationNotificationEmails, setSelfRegistrationNotificationEmails] = useState(
    DEFAULT_SELF_REGISTRATION_NOTIFICATION_EMAILS
  );
  const [isSavingSelfRegistrationNotifications, setIsSavingSelfRegistrationNotifications] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState<OrganizationType>("md");
  const [editContactEmail, setEditContactEmail] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const organization = useQuery(api.admin.organizations.get, {
    id: orgId as Id<"organizations">,
  });

  const archiveOrg = useMutation(api.admin.organizations.archive);
  const updateOrg = useMutation(api.admin.organizations.update);

  useEffect(() => {
    if (!organization) return;
    setEditName(organization.name);
    setEditType((organization.type as OrganizationType) ?? "md");
    setEditContactEmail(organization.contactEmail ?? "");
  }, [organization]);

  useEffect(() => {
    if (!organization?.settings) return;
    try {
      const parsed = JSON.parse(organization.settings);
      const value = Number(parsed.versichertenProfileRetentionDays);
      if (Number.isFinite(value) && value > 0) {
        setVersichertenProfileRetentionDays(String(Math.floor(value)));
      }
    } catch {
      setVersichertenProfileRetentionDays("180");
    }
  }, [organization?.settings]);

  useEffect(() => {
    if (!organization?.settings) return;
    try {
      const parsed = JSON.parse(organization.settings);
      const configured = parsed.selfRegistrationNotificationEmails ?? {};
      setSelfRegistrationNotificationEmails({
        plastischeChirurgie:
          configured.plastischeChirurgie?.trim() ||
          DEFAULT_SELF_REGISTRATION_NOTIFICATION_EMAILS.plastischeChirurgie,
        auOthers:
          configured.auOthers?.trim() ||
          DEFAULT_SELF_REGISTRATION_NOTIFICATION_EMAILS.auOthers,
        pflegebegutachtung:
          configured.pflegebegutachtung?.trim() ||
          DEFAULT_SELF_REGISTRATION_NOTIFICATION_EMAILS.pflegebegutachtung,
      });
    } catch {
      setSelfRegistrationNotificationEmails(
        DEFAULT_SELF_REGISTRATION_NOTIFICATION_EMAILS
      );
    }
  }, [organization?.settings]);

  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!organization) return;

    try {
      await archiveOrg({ id: orgId as Id<"organizations"> });
      toast({
        title: t("archiveSuccess.title"),
        description: t("archiveSuccess.description", { name: organization.name }),
      });
      navigate({ to: "/organizations" });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setArchiveDialogOpen(false);
    }
  };

  const handleManageLicense = () => {
    navigate({ to: "/licenses" });
  };

  const handleCancelEdit = () => {
    if (!organization) return;
    setEditName(organization.name);
    setEditType((organization.type as OrganizationType) ?? "md");
    setEditContactEmail(organization.contactEmail ?? "");
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!organization || !editName.trim()) return;

    setIsSavingEdit(true);
    try {
      await updateOrg({
        id: orgId as Id<"organizations">,
        name: editName.trim(),
        type: editType,
        contactEmail: editContactEmail.trim(),
      });
      toast({
        title: t("detail.edit.successTitle"),
        description: t("detail.edit.successDescription"),
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleSaveRetention = async () => {
    if (!organization) return;
    const nextValue = Math.max(1, Math.floor(Number(versichertenProfileRetentionDays) || 180));
    setIsSavingRetention(true);
    try {
      const parsed = organization.settings ? JSON.parse(organization.settings) : {};
      parsed.versichertenProfileRetentionDays = nextValue;
      await updateOrg({
        id: orgId as Id<"organizations">,
        settings: JSON.stringify(parsed),
      });
      toast({
        title: t("detail.retention.successTitle"),
        description: t("detail.retention.successDescription"),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSavingRetention(false);
    }
  };

  const handleSaveSelfRegistrationNotifications = async () => {
    if (!organization) return;

    setIsSavingSelfRegistrationNotifications(true);
    try {
      const parsed = organization.settings ? JSON.parse(organization.settings) : {};
      parsed.selfRegistrationNotificationEmails = {
        plastischeChirurgie:
          selfRegistrationNotificationEmails.plastischeChirurgie.trim(),
        auOthers: selfRegistrationNotificationEmails.auOthers.trim(),
        pflegebegutachtung:
          selfRegistrationNotificationEmails.pflegebegutachtung.trim(),
      };

      await updateOrg({
        id: orgId as Id<"organizations">,
        settings: JSON.stringify(parsed),
      });

      toast({
        title: t("detail.selfRegistrationNotifications.successTitle"),
        description: t(
          "detail.selfRegistrationNotifications.successDescription"
        ),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSavingSelfRegistrationNotifications(false);
    }
  };

  if (organization === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (organization === null) {
    return (
      <div className="flex h-64 flex-col items-center justify-center space-y-4">
        <AlertTriangle className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{t("detail.notFound")}</h2>
        <Link
          to="/organizations"
          className="inline-flex items-center gap-2 text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {tc("actions.back")}
        </Link>
      </div>
    );
  }

  const userCount = organization.users?.length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/organizations" className="rounded-lg p-2 hover:bg-muted">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{organization.name}</h1>
            <p className="text-muted-foreground">{organization.slug}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              <Edit className="h-4 w-4" />
              {tc("actions.edit")}
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEdit}
                className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                {t("detail.edit.cancel")}
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSavingEdit || !editName.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {isSavingEdit && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("detail.edit.save")}
              </button>
            </>
          )}
          <button
            onClick={handleArchiveClick}
            className="inline-flex items-center gap-2 rounded-lg border border-destructive/50 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            {tc("actions.archive")}
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            organization.status === "active"
              ? "bg-success-muted text-success-muted-foreground"
              : organization.status === "trial"
                ? "bg-info-muted text-info-muted-foreground"
                : organization.status === "suspended"
                  ? "bg-error-muted text-error-muted-foreground"
                  : "bg-muted text-muted-foreground"
          }`}
        >
          {getOrganizationStatusLabel(tc, organization.status)}
        </span>
        {organization.license && (
          <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            {organization.license.tier}
          </span>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">
              {isEditing ? t("detail.edit.title") : t("detail.details")}
            </h2>
          </div>
          {!isEditing ? (
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-muted-foreground">{t("detail.slug")}</dt>
                <dd className="font-medium">{organization.slug}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t("detail.type")}</dt>
                <dd className="font-medium">{getOrganizationTypeLabel(t, organization.type)}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t("detail.edit.contactEmail")}</dt>
                <dd className="font-medium">{organization.contactEmail || "—"}</dd>
              </div>
            </dl>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground" htmlFor="organization-edit-name">
                  {t("detail.edit.name")}
                </label>
                <input
                  id="organization-edit-name"
                  type="text"
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                  autoComplete="off"
                  className="mt-1 block w-full rounded-lg border border-input px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground" htmlFor="organization-edit-type">
                  {t("detail.edit.type")}
                </label>
                <select
                  id="organization-edit-type"
                  value={editType}
                  onChange={(event) => setEditType(event.target.value as OrganizationType)}
                  autoComplete="off"
                  className="mt-1 block w-full rounded-lg border border-input px-3 py-2"
                >
                  <option value="md">{t("create.types.md")}</option>
                  <option value="clinic">{t("create.types.clinic")}</option>
                  <option value="practice">{t("create.types.practice")}</option>
                  <option value="insurance">{t("create.types.insurance")}</option>
                  <option value="other">{t("create.types.other")}</option>
                </select>
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-foreground"
                  htmlFor="organization-edit-contact-email"
                >
                  {t("detail.edit.contactEmail")}
                </label>
                <input
                  id="organization-edit-contact-email"
                  type="email"
                  value={editContactEmail}
                  onChange={(event) => setEditContactEmail(event.target.value)}
                  autoComplete="off"
                  placeholder={t("detail.edit.contactEmailPlaceholder")}
                  className="mt-1 block w-full rounded-lg border border-input px-3 py-2"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("detail.edit.contactEmailHelp")}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <Key className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">{t("detail.license.title")}</h2>
          </div>
          {organization.license ? (
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-muted-foreground">{t("detail.license.tier")}</dt>
                <dd className="font-medium capitalize">{organization.license.tier}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t("detail.license.validUntil")}</dt>
                <dd className="font-medium">{formatShortDate(organization.license.validUntil)}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t("detail.license.modules")}</dt>
                <dd className="mt-1 flex flex-wrap gap-2">
                  {organization.license.modules && organization.license.modules.length > 0 ? (
                    organization.license.modules.map((module: string) => (
                      <span
                        key={module}
                        className="inline-flex rounded bg-muted px-2 py-1 text-xs font-medium"
                      >
                        {module}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t("detail.license.limits")}</dt>
                <dd className="font-medium">{t("detail.license.unlimited")}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-muted-foreground">{t("table.noLicense")}</p>
          )}
          <div className="mt-4 border-t pt-4">
            <button
              onClick={handleManageLicense}
              className="text-sm text-primary hover:text-primary/80"
            >
              {t("detail.license.manage")} →
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">
              {t("detail.users.count", { count: userCount })}
            </h2>
          </div>
          <Link
            to="/users"
            search={{ org: orgId }}
            className="text-sm text-primary hover:text-primary/80"
          >
            {t("detail.users.viewAll")} →
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("detail.users.description", { count: userCount })}
        </p>
      </div>

      <div className="space-y-3 rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold">{t("detail.retention.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("detail.retention.description")}</p>
        <div className="flex max-w-sm items-center gap-3">
          <input
            type="number"
            min={1}
            value={versichertenProfileRetentionDays}
            onChange={(event) => setVersichertenProfileRetentionDays(event.target.value)}
            autoComplete="off"
            className="h-10 w-32 rounded-md border border-input bg-background px-3 text-sm"
          />
          <button
            onClick={handleSaveRetention}
            disabled={isSavingRetention}
            className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isSavingRetention ? t("detail.retention.saving") : t("detail.retention.save")}
          </button>
        </div>
      </div>

      <div className="space-y-4 rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold">
          {t("detail.selfRegistrationNotifications.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("detail.selfRegistrationNotifications.description")}
        </p>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <label
              htmlFor="self-registration-notification-plastische"
              className="text-sm font-medium text-foreground"
            >
              {t("detail.selfRegistrationNotifications.plastischeChirurgie")}
            </label>
            <input
              id="self-registration-notification-plastische"
              type="email"
              value={selfRegistrationNotificationEmails.plastischeChirurgie}
              onChange={(event) =>
                setSelfRegistrationNotificationEmails((current) => ({
                  ...current,
                  plastischeChirurgie: event.target.value,
                }))
              }
              autoComplete="off"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="self-registration-notification-au"
              className="text-sm font-medium text-foreground"
            >
              {t("detail.selfRegistrationNotifications.auOthers")}
            </label>
            <input
              id="self-registration-notification-au"
              type="email"
              value={selfRegistrationNotificationEmails.auOthers}
              onChange={(event) =>
                setSelfRegistrationNotificationEmails((current) => ({
                  ...current,
                  auOthers: event.target.value,
                }))
              }
              autoComplete="off"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="self-registration-notification-pflege"
              className="text-sm font-medium text-foreground"
            >
              {t("detail.selfRegistrationNotifications.pflegebegutachtung")}
            </label>
            <input
              id="self-registration-notification-pflege"
              type="email"
              value={selfRegistrationNotificationEmails.pflegebegutachtung}
              onChange={(event) =>
                setSelfRegistrationNotificationEmails((current) => ({
                  ...current,
                  pflegebegutachtung: event.target.value,
                }))
              }
              autoComplete="off"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>
        </div>

        <div>
          <button
            onClick={handleSaveSelfRegistrationNotifications}
            disabled={isSavingSelfRegistrationNotifications}
            className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isSavingSelfRegistrationNotifications
              ? t("detail.selfRegistrationNotifications.saving")
              : t("detail.selfRegistrationNotifications.save")}
          </button>
        </div>
      </div>

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("archiveDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("archiveDialog.description", { name: organization.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmArchive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tc("actions.archive")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function OrganizationDetail() {
  return (
    <SuperAdminGuard>
      <OrganizationDetailContent />
    </SuperAdminGuard>
  );
}
